import { useCallback, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { presignStorefrontUpload } from '@/services/api/storefront-content.api';
import { extractErrorMessage } from '@/lib/error';

/** Client-side guards mirroring the server allow-list + per-kind size caps. */
const ALLOWED: Record<UploadKind, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/webp'],
  video: ['video/mp4', 'video/webm'],
};
const MAX_BYTES: Record<UploadKind, number> = {
  image: 10 * 1024 * 1024,
  video: 200 * 1024 * 1024,
};

export type UploadKind = 'image' | 'video';

export type UploadState =
  | { phase: 'idle' }
  | { phase: 'requesting-url' }
  | { phase: 'uploading'; pct: number }
  | { phase: 'committing' }
  | { phase: 'done'; publicUrl: string }
  | { phase: 'error'; message: string }
  | { phase: 'no-keys' }; // presign returned 503 → paste-URL fallback

function extFromFile(file: File): string {
  const fromName = file.name.split('.').pop();
  if (fromName && /^[a-zA-Z0-9]{1,8}$/.test(fromName)) return fromName.toLowerCase();
  const fromType = file.type.split('/').pop() ?? 'bin';
  return fromType.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8) || 'bin';
}

/**
 * Presigned-upload state machine (TASK-62), reused for poster (image) + video.
 * NOT a React-Query cache entry. `start(file)`:
 *   0. client-side type/size guard → 'error' before any request
 *   1. POST presign → 503 becomes 'no-keys' (caller swaps to paste-URL)
 *   2. raw axios PUT of the bytes to storage (no auth header / app baseURL),
 *      streaming progress %
 *   3. 'done' with the public URL the form submits as posterUrl/videoUrl
 */
export function usePresignUpload(kind: UploadKind) {
  const [state, setState] = useState<UploadState>({ phase: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState({ phase: 'idle' });
  }, []);

  const start = useCallback(
    async (file: File) => {
      // 0. client-side guards
      if (!ALLOWED[kind].includes(file.type)) {
        setState({
          phase: 'error',
          message: `Unsupported ${kind} type. Allowed: ${ALLOWED[kind].join(', ')}`,
        });
        return;
      }
      if (file.size > MAX_BYTES[kind]) {
        const mb = Math.round(MAX_BYTES[kind] / (1024 * 1024));
        setState({ phase: 'error', message: `File exceeds the ${mb} MB ${kind} limit.` });
        return;
      }

      // 1. request the presigned URL
      setState({ phase: 'requesting-url' });
      let presign;
      try {
        presign = await presignStorefrontUpload({
          kind,
          contentType: file.type,
          ext: extFromFile(file),
          sizeBytes: file.size,
        });
      } catch (err) {
        const status = (err as AxiosError).response?.status;
        if (status === 503) {
          setState({ phase: 'no-keys' });
          return;
        }
        setState({ phase: 'error', message: extractErrorMessage(err) });
        return;
      }

      // 2. PUT the raw bytes straight to storage (no app interceptors / auth)
      const controller = new AbortController();
      abortRef.current = controller;
      setState({ phase: 'uploading', pct: 0 });
      try {
        await axios.put(presign.uploadUrl, file, {
          headers: { 'Content-Type': file.type },
          signal: controller.signal,
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setState({ phase: 'uploading', pct });
          },
        });
      } catch (err) {
        if (axios.isCancel(err) || (err as AxiosError).code === 'ERR_CANCELED') {
          setState({ phase: 'idle' });
          return;
        }
        setState({ phase: 'error', message: extractErrorMessage(err) });
        return;
      } finally {
        abortRef.current = null;
      }

      // 3. commit — hand the public URL back to the form
      setState({ phase: 'committing' });
      setState({ phase: 'done', publicUrl: presign.publicUrl });
    },
    [kind],
  );

  return { state, start, reset };
}
